import React, { type PropsWithChildren } from 'react';
import { Platform, StyleSheet, useWindowDimensions } from 'react-native';

import { UL } from '@expo/html-elements';
import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

export const List = (props: Props) => {
  const { children, withoutSpacing, testID } = props;

  const { fontScale, width } = useWindowDimensions();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isExtraSmallScreen = width < 375;
  const isScaledFontSize = fontScale >= 1.3;

  const shouldRenderNarrowVariant = isExtraSmallScreen || isScaledFontSize;

  // ─── Styles ──────────────────────────────────────────────────────────

  const listStyles = [
    styles.list,
    shouldRenderNarrowVariant ? styles.listNarrow : styles.listWide,
    withoutSpacing ? styles.listWithoutSpacing : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <UL style={listStyles} testID={testID}>
      {children}
    </UL>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  list: {
    display: 'flex', // override default style
    flexDirection: 'row',
    justifyContent: 'space-around',
    minWidth: '100%',
    paddingVertical: theme.spacing['2'],
    paddingRight: Platform.select({
      web: undefined,
      default: 84, // we use extra offset, since `minWidth: 100%` is not working as expected on native.
    }),
  },
  listNarrow: {
    gap: theme.spacing['3'],
    paddingHorizontal: theme.spacing['4'],
  },
  listWide: {
    gap: theme.spacing['5'],
    paddingHorizontal: theme.spacing['6'],
  },
  listWithoutSpacing: {
    paddingHorizontal: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  testID?: string;
  withoutSpacing?: boolean;
}>;
